import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Paragraph, Title, Headline, Dropdown } from '@cambridgeassessment/cambridge-ui';
import { isEqual } from 'lodash';
import { StyledContainer } from '../Content.styles';
import { StyledInputContainer } from './Series.styles';
import { RootState } from '../../../store';
import Loader from '../../../components/Loader/Loader';
import thunks from '../../../store/series/thunks';
import { Series } from '../../../types/Series';
import { SubmissionWindow } from '../../../types/Series';
import Details from './Details';
import DeletionDate from './DeletionDate';
import StartDate from './StartDate/StartDate';

const SeriesFC: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { businessUnit } = useSelector((state: RootState) => state.userData);
  const { isLoading, seriesData, seriesDetails, hasError } = useSelector((state: RootState) => state.series);
  const [selectedSeries, setSelectedSeries] = useState<Series | null>(null);

  const setAndFetchSeriesDetails = (series: Series): void => {
    setSelectedSeries(series);
    if (businessUnit) dispatch(thunks.getDetailedSeries(businessUnit.id, series.id));
  };

  useEffect(() => {
    if (businessUnit) dispatch(thunks.getSeriesData(businessUnit.id));
  }, [businessUnit, dispatch]);

  useEffect(() => {
    if (!selectedSeries && seriesData && seriesData[0] && businessUnit) {
      setSelectedSeries(seriesData[0]);
      dispatch(thunks.getDetailedSeries(businessUnit.id, seriesData[0].id));
    }
  }, [businessUnit, seriesData, dispatch, selectedSeries]);

  if (isLoading && !selectedSeries) return <Loader />;

  if (hasError) return <div>Failed to get Series data</div>;

  const onSeriesSelected = (series: Series): void => {
    if (!isEqual(selectedSeries, series)) {
      setAndFetchSeriesDetails(series);
    }
  };

  const renderSeries = (item: Series): string => item.name;

  const renderSubmissionWindowDetails = (window: SubmissionWindow, index: number): React.ReactElement => {
    return (
      <Details
        key={`window-${window.name}-${index}`}
        {...window}
        end={selectedSeries?.endDate}
        seriesId={seriesDetails?._id}
        businessUnitId={businessUnit?.id}
      />
    );
  };

  return (
    <StyledContainer data-qa-id='page-priming-series'>
      <Headline>Series business rules</Headline>
      <Paragraph>Define and set requirements for current and future series.</Paragraph>
      <Title id='series'>Select series</Title>
      {selectedSeries && (
        <StyledInputContainer aria-label='select-series-business-rules'>
          <Dropdown<Series>
            data={seriesData as Series[]}
            renderOption={renderSeries}
            renderSelectedOption={renderSeries}
            dataQaId='series-select'
            onChange={onSeriesSelected}
            value={selectedSeries}
            placeholder={'Series'}
            title='Select series'
          />
        </StyledInputContainer>
      )}
      {selectedSeries && seriesDetails && (
        <StartDate
          series={selectedSeries}
          startDate={seriesDetails?.startDate}
          componentSeriesStartDates={seriesDetails?.componentSeriesStartDates}
        />
      )}
      {selectedSeries && seriesDetails && seriesDetails.submissionWindows.map(renderSubmissionWindowDetails)}
      <DeletionDate series={selectedSeries} deletionDate={seriesDetails?.deletionDate} />
    </StyledContainer>
  );
};

export default SeriesFC;
